import { useEffect, useState } from 'react';
import {
  ParkingLot,
  ParkingLotSectionDriveIn,
  ParkingLotSectionSeasonsPrices,
  ParkingLotSectionWithReservation,
  filterClosestParkingWithReservation,
  filterCoveredPremiumParkingWithReservation,
  filterEconomyParkingWithReservation,
  filterPremiumParkingWithReservation,
  getClosestParkingsDriveIn,
  getCoveredPremiumParkingsDriveIn,
  getPremiumParkinsgDriveIn,
  getSeasonsPricesByParkingLotSectionDriveIn,
  getSeasonsPricesByParkingLotSectionWithReservation,
  getStandardParkingsDriveIn,
  getStreetParkingsDriveIn,
} from '../../../domain/parking';
import { ParkingService } from '../../../services/parking';

interface ParkingLots {
  parkingLotsWithReservation: {
    section: ParkingLotSectionWithReservation;
    seasonsPrices: ParkingLotSectionSeasonsPrices;
    lots: ParkingLot[];
  }[];
  parkingLotsDriveIn: {
    section: ParkingLotSectionDriveIn;
    seasonsPrices: ParkingLotSectionSeasonsPrices;
    lots: ParkingLot[];
  }[];
}

export const useParkingLots = (selector = true) => {
  const [parkingLots, setParkingLots] = useState<ParkingLots>({
    parkingLotsWithReservation: [],
    parkingLotsDriveIn: [],
  });

  const fetchParkingLots = async () => {
    try {
      const service = new ParkingService();
      let lots = [];
      if (selector) {
        lots = await service.getParkingLots();
      }

      /** Parking lot section with reservation */

      const closestParkingLotsWithReservation = filterClosestParkingWithReservation(lots);
      const coveredPremiumParkingLotsWithReservation = filterCoveredPremiumParkingWithReservation(lots);
      const premiumParkingLotsWithReservation = filterPremiumParkingWithReservation(lots);
      const economyParkingLotsWithReservation = filterEconomyParkingWithReservation(lots);

      const closestParkingWithReservationSeasonsPrices = getSeasonsPricesByParkingLotSectionWithReservation(
        ParkingLotSectionWithReservation.Closest,
      );
      const coveredPremiumParkingWithReservationSeasonsPrices = getSeasonsPricesByParkingLotSectionWithReservation(
        ParkingLotSectionWithReservation.CoveredPremium,
      );
      const premiumParkingWithReservationSeasonsPrices = getSeasonsPricesByParkingLotSectionWithReservation(
        ParkingLotSectionWithReservation.Premium,
      );
      const economyParkingWithReservationSeasonsPrices = getSeasonsPricesByParkingLotSectionWithReservation(
        ParkingLotSectionWithReservation.Economy,
      );

      const parkingLotsWithReservation = [
        {
          section: ParkingLotSectionWithReservation.Closest,
          seasonsPrices: closestParkingWithReservationSeasonsPrices,
          lots: closestParkingLotsWithReservation,
        },
        {
          section: ParkingLotSectionWithReservation.CoveredPremium,
          seasonsPrices: coveredPremiumParkingWithReservationSeasonsPrices,
          lots: coveredPremiumParkingLotsWithReservation,
        },
        {
          section: ParkingLotSectionWithReservation.Premium,
          seasonsPrices: premiumParkingWithReservationSeasonsPrices,
          lots: premiumParkingLotsWithReservation,
        },
        {
          section: ParkingLotSectionWithReservation.Economy,
          seasonsPrices: economyParkingWithReservationSeasonsPrices,
          lots: economyParkingLotsWithReservation,
        },
      ];

      /** Parking lot section drive-in */

      const closestParkingLotsDrivenIn = getClosestParkingsDriveIn();
      const coveredPremiumParkingLotsDriveIn = getCoveredPremiumParkingsDriveIn();
      const premiumParkingLotsDriveIn = getPremiumParkinsgDriveIn();
      const standardParkingLotsWithDriveIn = getStandardParkingsDriveIn();
      const streetParkingLotsWithDriveIn = getStreetParkingsDriveIn();

      const closestParkingDriveInSeasonsPrices = getSeasonsPricesByParkingLotSectionDriveIn(
        ParkingLotSectionDriveIn.Closest,
      );
      const coveredPremiumParkingDriveInSeasonsPrices = getSeasonsPricesByParkingLotSectionDriveIn(
        ParkingLotSectionDriveIn.CoveredPremium,
      );
      const premiumParkingDriveInSeasonsPrices = getSeasonsPricesByParkingLotSectionDriveIn(
        ParkingLotSectionDriveIn.Premium,
      );
      const standardParkingDrivenInSeasonsPrices = getSeasonsPricesByParkingLotSectionDriveIn(
        ParkingLotSectionDriveIn.Standard,
      );
      const streetParkingDriveInSeasonsPrices = getSeasonsPricesByParkingLotSectionDriveIn(
        ParkingLotSectionDriveIn.Street,
      );

      const parkingLotsDriveIn = [
        {
          section: ParkingLotSectionDriveIn.Closest,
          seasonsPrices: closestParkingDriveInSeasonsPrices,
          lots: closestParkingLotsDrivenIn,
        },
        {
          section: ParkingLotSectionDriveIn.CoveredPremium,
          seasonsPrices: coveredPremiumParkingDriveInSeasonsPrices,
          lots: coveredPremiumParkingLotsDriveIn,
        },
        {
          section: ParkingLotSectionDriveIn.Premium,
          seasonsPrices: premiumParkingDriveInSeasonsPrices,
          lots: premiumParkingLotsDriveIn,
        },
        {
          section: ParkingLotSectionDriveIn.Standard,
          seasonsPrices: standardParkingDrivenInSeasonsPrices,
          lots: standardParkingLotsWithDriveIn,
        },
        {
          section: ParkingLotSectionDriveIn.Street,
          seasonsPrices: streetParkingDriveInSeasonsPrices,
          lots: streetParkingLotsWithDriveIn,
        },
      ];

      setParkingLots({ parkingLotsWithReservation, parkingLotsDriveIn });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchParkingLots();
  }, []);

  return parkingLots;
};
